import { useState } from 'react';
import { Box } from '@mui/material';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MuiToggleButton from '@mui/material/ToggleButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EbvPercentileGraph, {
  type EbvChartItem,
} from '@/components/publicSearch/animal/ebv/EbvPercentileGraph';
import EbvPotentialChangeGraph from '@/components/publicSearch/animal/ebv/EbvPotentialChangeGraph';
import type { TraitExplanationEntry } from '@/components/publicSearch/animal/ebv/ebvTraitExplanations';

type AnimalEbvTabChartsProps = {
  showPercentileGraph: boolean;
  showPotentialChangeGraph: boolean;
  ebvChartData: EbvChartItem[];
  percentileGraphTitle: string;
  potentialChangeGraphTitle: string;
  ebvLabel: string;
  breedAverageMessage: string;
  traitExplanationsByKey: Map<string, TraitExplanationEntry>;
  showBreedplanHelp: boolean;
  useColourSlotGrouping?: boolean;
};

export default function AnimalEbvTabCharts({
  showPercentileGraph,
  showPotentialChangeGraph,
  ebvChartData,
  percentileGraphTitle,
  potentialChangeGraphTitle,
  ebvLabel,
  breedAverageMessage,
  traitExplanationsByKey,
  showBreedplanHelp,
  useColourSlotGrouping = false,
}: AnimalEbvTabChartsProps) {
  const [ebvChartView, setEbvChartView] = useState<
    'percentile' | 'potentialChange'
  >('percentile');
  const [ebvPercentileInfoOpen, setEbvPercentileInfoOpen] =
    useState<boolean>(false);
  const [ebvPotentialChangeInfoOpen, setEbvPotentialChangeInfoOpen] =
    useState<boolean>(false);

  // BREEDPLAN (BLS-318): the Percentile and Potential Change graphs must never be
  // shown side by side, whatever the viewport, because placing them together
  // invites a direct comparison that is not valid. One graph at a time, behind
  // the toggle.
  return (
    <Box>
      {showPercentileGraph && showPotentialChangeGraph && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: { xs: 1, md: 2 },
          }}
        >
          <ToggleButtonGroup
            value={ebvChartView}
            exclusive
            onChange={(_, newValue) => {
              if (newValue) setEbvChartView(newValue);
            }}
            size="small"
            sx={{
              '& .MuiToggleButtonGroup-grouped': {
                border: 1,
                borderColor: 'divider',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  color: 'text.primary',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                },
              },
            }}
          >
            <MuiToggleButton value="percentile">
              {percentileGraphTitle}
              <Box
                component="span"
                role="button"
                aria-label={`About the ${percentileGraphTitle}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setEbvPercentileInfoOpen(true);
                }}
                sx={{
                  display: {
                    xs: 'inline-flex',
                    md: 'none',
                  },
                  alignItems: 'center',
                  ml: 0.75,
                  opacity: 0.6,
                  '&:hover': { opacity: 1 },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
            </MuiToggleButton>
            <MuiToggleButton value="potentialChange">
              {potentialChangeGraphTitle}
              <Box
                component="span"
                role="button"
                aria-label={`About the ${potentialChangeGraphTitle}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setEbvPotentialChangeInfoOpen(true);
                }}
                sx={{
                  display: {
                    xs: 'inline-flex',
                    md: 'none',
                  },
                  alignItems: 'center',
                  ml: 0.75,
                  opacity: 0.6,
                  '&:hover': { opacity: 1 },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
            </MuiToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      <Box
        sx={{
          maxWidth: (() => {
            if (showPercentileGraph && showPotentialChangeGraph) {
              return ebvChartView === 'percentile' ? 1000 : 1150;
            }
            return showPotentialChangeGraph ? 1150 : 1000;
          })(),
          width: '100%',
          mx: 'auto',
        }}
      >
        {(() => {
          if (
            showPercentileGraph &&
            (!showPotentialChangeGraph || ebvChartView === 'percentile')
          ) {
            return (
              <EbvPercentileGraph
                data={ebvChartData}
                title={percentileGraphTitle}
                ebvLabel={ebvLabel}
                breedAverageMessage={breedAverageMessage}
                traitExplanationsByKey={traitExplanationsByKey}
                showBreedplanHelp={showBreedplanHelp}
                infoOpen={ebvPercentileInfoOpen}
                onInfoClose={() => setEbvPercentileInfoOpen(false)}
                useColourSlotGrouping={useColourSlotGrouping}
              />
            );
          }
          if (showPotentialChangeGraph) {
            return (
              <EbvPotentialChangeGraph
                data={ebvChartData}
                title={potentialChangeGraphTitle}
                ebvLabel={ebvLabel}
                breedAverageMessage={breedAverageMessage}
                traitExplanationsByKey={traitExplanationsByKey}
                showBreedplanHelp={showBreedplanHelp}
                infoOpen={ebvPotentialChangeInfoOpen}
                onInfoClose={() => setEbvPotentialChangeInfoOpen(false)}
                useColourSlotGrouping={useColourSlotGrouping}
              />
            );
          }
          return null;
        })()}
      </Box>
    </Box>
  );
}
